'use client';

import React, { useRef, useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui/shadcn/button';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { EmptyState } from '@/shared/ui';
import { Search, History, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import type { HistoryItem } from '../model/history.types';
import { ConceptCard } from '@/features/design/concept';
import { AlbumCard } from './AlbumCard';
import { EstimateHistoryCard } from './EstimateHistoryCard';
import type { SubtypeFilter } from '../hooks/use-history-filters';

interface HistoryListProps {
  items: HistoryItem[];
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  searchQuery?: string;
  hasActiveFilters?: boolean;
  subtype?: SubtypeFilter;
  onClearFilters?: () => void;
  onFavoriteToggle?: (conceptId: string, isFavorite: boolean) => void;
}

export function HistoryList({
  items,
  isLoading = false,
  isFetchingNextPage = false,
  hasNextPage = false,
  fetchNextPage,
  searchQuery = '',
  hasActiveFilters = false,
  subtype = 'all',
  onClearFilters,
  onFavoriteToggle,
}: HistoryListProps) {
  const t = useTranslations('history');
  const router = useRouter();
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (!fetchNextPage || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleGoToProjects = useCallback(() => {
    router.push('/');
  }, [router]);

  // Loading state
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-4 md:gap-6">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-48 rounded-lg" />
        ))}
      </div>
    );
  }

  // Empty state - no results with filters
  if (items.length === 0 && (searchQuery || hasActiveFilters)) {
    return (
      <EmptyState
        icon={<Search />}
        title={t('emptyStates.noResults.title')}
        description={t('emptyStates.noResults.description')}
        action={
          onClearFilters
            ? {
                label: t('emptyStates.noResults.button'),
                onClick: onClearFilters,
              }
            : undefined
        }
        withCard
        centered
      />
    );
  }

  // Empty state - no renders at all.
  // Only the title narrows to the active subtype tab; the description stays shared
  // across all tabs (renders + blueprints).
  if (items.length === 0) {
    const emptyTitleKey =
      subtype === 'render'
        ? 'emptyStates.noRenders.titleRenders'
        : subtype === 'album'
          ? 'emptyStates.noRenders.titleBlueprints'
          : subtype === 'estimate'
            ? 'emptyStates.noRenders.titleEstimates'
            : 'emptyStates.noRenders.title';
    return (
      <EmptyState
        icon={<History />}
        title={t(emptyTitleKey)}
        description={t('emptyStates.noRenders.description')}
        action={{
          label: t('emptyStates.noRenders.button'),
          onClick: handleGoToProjects,
        }}
        withCard
        centered
      />
    );
  }

  // Note: Virtualization disabled for grid layouts due to complexity
  // Grid layouts with multiple columns don't work well with simple vertical virtualization
  // For better performance with large datasets, use regular infinite scroll instead

  // Regular grid for smaller datasets
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-4 md:gap-6">
        {items.map((item) =>
          item.attributes.is_album ? (
            <AlbumCard key={item.id} item={item} />
          ) : item.attributes.is_estimate ? (
            <EstimateHistoryCard key={item.id} item={item} />
          ) : (
            <ConceptCard
              key={item.id}
              concept={item}
              projectId={item.relationships.project.data.id}
              projectName={item.relationships.project.meta.name}
              onFavoriteToggle={onFavoriteToggle}
            />
          )
        )}
      </div>

      {/* Load more trigger for infinite scroll */}
      {hasNextPage && (
        <div ref={loadMoreRef} className="flex justify-center py-4">
          {isFetchingNextPage ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : (
            <Button
              variant="outline"
              onClick={fetchNextPage}
              disabled={!hasNextPage || isFetchingNextPage}
            >
              {t('loadMore')}
            </Button>
          )}
        </div>
      )}
    </>
  );
}
